// Admin "Genre Review" tool — surfaces tracks where the taxonomy migration
// fell back to "Other" or where main_genre is missing. Each row gets:
//   - track title / artist / original genre
//   - suggested main genre (with a low-confidence flag when guessed)
//   - dropdown to override the main genre
//   - editable style + mood tags
//   - per-row "Save" or top-level "Apply all"
//
// Pass-1 scope: focused, no analytics. Pass-2 will tie the same component
// into broader inventory management once we stabilize the data layer.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError, coverUrl } from "../lib/api";
import { ListChecks, RefreshCw, AlertTriangle, Save, Check } from "lucide-react";
import { MAIN_GENRES, STYLE_TAG_SUGGESTIONS, MOOD_TAG_SUGGESTIONS } from "../lib/taxonomy";
import TagChipsInput from "./TagChipsInput";

const DEFAULT_COVER = "https://images.unsplash.com/photo-1771747131849-41056d3d9be3?crop=entropy&cs=srgb&fm=jpg&w=200&q=80";

export default function GenreReviewTool() {
  const [items, setItems] = useState([]);
  const [edits, setEdits] = useState({}); // { trackId: { main_genre, style_tags, mood_tags, dirty } }
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [savingAll, setSavingAll] = useState(false);
  const [savedFlash, setSavedFlash] = useState(0);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/tracks/genre-review");
      setItems(data || []);
      // Seed edits with the suggested main_genre so admin can accept-with-one-click.
      const seed = {};
      for (const t of data || []) {
        seed[t.id] = {
          main_genre: t.main_genre || t.suggested_main_genre || "Other",
          style_tags: t.style_tags || [],
          mood_tags: t.mood_tags || [],
          dirty: false,
        };
      }
      setEdits(seed);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirtyIds = useMemo(
    () => Object.entries(edits).filter(([, v]) => v.dirty).map(([id]) => id),
    [edits],
  );

  const updateEdit = (id, partial) => {
    setEdits((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...partial, dirty: true },
    }));
  };

  const saveOne = async (id) => {
    const e = edits[id];
    if (!e) return;
    try {
      await api.patch(`/admin/tracks/${id}`, {
        main_genre: e.main_genre,
        style_tags: e.style_tags,
        mood_tags: e.mood_tags,
      });
      // Drop the row from the queue locally
      setItems((prev) => prev.filter((t) => t.id !== id));
      setSavedFlash((n) => n + 1);
    } catch (er) {
      setErr(formatApiError(er));
    }
  };

  const saveAll = async () => {
    if (dirtyIds.length === 0) return;
    setSavingAll(true); setErr("");
    try {
      const items = dirtyIds.map((id) => ({
        track_id: id,
        main_genre: edits[id].main_genre,
        style_tags: edits[id].style_tags,
        mood_tags: edits[id].mood_tags,
      }));
      const { data } = await api.post("/admin/tracks/genre-review/bulk", { items });
      // Refresh — the backend already cleared `needs_genre_review` for these.
      await load();
      setSavedFlash((n) => n + (data?.updated || 0));
    } catch (er) {
      setErr(formatApiError(er));
    } finally {
      setSavingAll(false);
    }
  };

  return (
    <section className="space-y-4" data-testid="genre-review-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-serif text-2xl text-[#FDFBF7] tracking-tight flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
          Genre review
        </h2>
        <div className="flex items-center gap-2">
          {savedFlash > 0 && (
            <span className="text-xs text-emerald-400 inline-flex items-center gap-1">
              <Check className="w-3 h-3" strokeWidth={2} /> Saved
            </span>
          )}
          <button onClick={load} className="btn-ghost text-sm" data-testid="genre-review-refresh">
            <RefreshCw className="w-4 h-4 inline mr-1" strokeWidth={1.5} /> Refresh
          </button>
          <button
            onClick={saveAll}
            disabled={dirtyIds.length === 0 || savingAll}
            className="btn-primary text-sm disabled:opacity-40"
            data-testid="genre-review-save-all"
          >
            <Save className="w-4 h-4" strokeWidth={1.5} /> Apply {dirtyIds.length || "all"}
          </button>
        </div>
      </div>

      {err && <div className="text-red-400 text-sm">{err}</div>}

      {loading ? (
        <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-24 rounded-lg shimmer" />)}</div>
      ) : items.length === 0 ? (
        <div className="card-surface p-10 text-center" data-testid="genre-review-empty">
          <Check className="w-8 h-8 text-emerald-400 mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-[#FDFBF7] font-serif text-xl">All tracks are correctly tagged</div>
          <div className="text-[#A1A4AB] text-sm mt-1">Nothing in the genre-review queue. New uploads with auto-mapped genres will appear here if confidence is low.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="genre-review-list">
          {items.map((t) => {
            const e = edits[t.id] || {};
            return (
              <div key={t.id} className="card-surface p-4" data-testid={`genre-review-row-${t.id}`}>
                <div className="grid md:grid-cols-[64px_1fr_240px_auto] gap-4 items-start">
                  <img
                    src={coverUrl(t) || DEFAULT_COVER}
                    alt=""
                    className="w-16 h-16 rounded-md border border-[#22262E] object-cover bg-[#111317]"
                  />
                  <div className="min-w-0">
                    <div className="text-[#FDFBF7] font-serif text-base truncate">{t.title}</div>
                    <div className="text-[#A1A4AB] text-sm truncate">{t.artist}</div>
                    <div className="text-xs text-[#6C707A] mt-1 truncate">
                      Original: <span className="text-[#A1A4AB]">{t.genre || "(no genre text)"}</span>
                      {t.suggested_low_confidence && (
                        <span className="ml-2 inline-flex items-center gap-1 text-amber-400">
                          <AlertTriangle className="w-3 h-3" strokeWidth={1.5} /> low confidence
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="overline text-[10px] text-[#A1A4AB] block mb-1">Main genre</label>
                    <select
                      value={e.main_genre || ""}
                      onChange={(ev) => updateEdit(t.id, { main_genre: ev.target.value })}
                      className="input-lux !py-1.5 text-sm"
                      data-testid={`genre-review-select-${t.id}`}
                    >
                      {MAIN_GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div className="flex md:flex-col gap-2 items-end">
                    <button
                      onClick={() => saveOne(t.id)}
                      className="btn-primary text-sm !px-3 !py-1.5"
                      data-testid={`genre-review-save-${t.id}`}
                    >
                      <Save className="w-3.5 h-3.5" strokeWidth={1.5} /> Save
                    </button>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3 mt-3">
                  <TagChipsInput
                    label="Style / subgenre tags"
                    value={e.style_tags || []}
                    onChange={(v) => updateEdit(t.id, { style_tags: v })}
                    suggestions={STYLE_TAG_SUGGESTIONS}
                    placeholder="Add style tags…"
                    testId={`genre-review-style-${t.id}`}
                  />
                  <TagChipsInput
                    label="Mood tags"
                    value={e.mood_tags || []}
                    onChange={(v) => updateEdit(t.id, { mood_tags: v })}
                    suggestions={MOOD_TAG_SUGGESTIONS}
                    placeholder="Add mood tags…"
                    testId={`genre-review-mood-${t.id}`}
                    maxItems={10}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
